import { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

const propTypes = {
  hasError: PropTypes.bool,
  noOfInputs: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isSecure: PropTypes.bool,
  inputSize: PropTypes.number,
  success: PropTypes.bool,
}

const defaultProps = {
  hasError: false,
  noOfInputs: 5,
  value: '',
  isSecure: false,
  inputSize: 56,
  success: false,
}

const OTPInputField = ({
  hasError,
  noOfInputs,
  value,
  onChange,
  isSecure,
  inputSize,
  success,
}) => {
  const inputRefs = useRef([])

  useEffect(() => {
    if (hasError) {
      inputRefs.current[0]?.focus()
    }
  }, [hasError])

  const handleChange = (e, index) => {
    const val = e.target.value

    // Only allow single digit
    if (val.length > 1) return

    // Only allow numbers
    if (val && !/^\d$/.test(val)) return

    const newValue = value.split('')
    while (newValue.length < noOfInputs) {
      newValue.push('')
    }
    newValue[index] = val

    onChange(newValue.join(''))

    // Auto-focus next input
    if (val && index < noOfInputs - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }

      const newValue = value.split('')
      while (newValue.length < noOfInputs) {
        newValue.push('')
      }
      newValue[index] = ''
      onChange(newValue.join(''))
    }

    // Arrow key navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (e.key === 'ArrowRight' && index < noOfInputs - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').trim()
    const digits = pastedData.replace(/\D/g, '').slice(0, noOfInputs)

    if (digits) {
      onChange(digits)
      const nextIndex = Math.min(digits.length, noOfInputs - 1)
      inputRefs.current[nextIndex]?.focus()
    }
  }

  const handleFocus = (e) => {
    // Select text on focus for easier replacement
    e.target.select()
  }

  return (
    <>
      <style>{`
        .otp-input-field {
          width: ${inputSize - 5}px;
          height: ${inputSize}px;
          text-align: center;
          font-size: 0.75rem;
          font-weight: 500;
          border-radius: 8px;
          box-shadow: 0px 1.11px 2.23px 0px #1018280F;
          box-shadow: 0px 1.11px 3.34px 0px #1018281A;
          border: 1px solid #EAECF0;
          background-color: #FFFFFF;
          color: #344054;
          transition: all 0.2s ease;
          caret-color: transparent;
        }

        .otp-input-field:hover:not(:disabled) {
          border-color: #A6F4C5;
        }

        .otp-input-field:focus {
          outline: none;
          border-color: #A6F4C5;
          background-color: #fff;
          box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.12);
        }

        .otp-input-field.filled-input {
          border-color: #A6F4C5;
          background-color: #fff;
          color: #6CE9A6;
        }

        .otp-input-field.filled-input:focus {
          border-color: #4ade80;
          box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.12);
        }

        .otp-input-field.error-input {
          border: 2px solid #ef4444 !important;
          background-color: #fef2f2;
          color: #ef4444;
        }

        .otp-input-field.error-input:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
        }

        .otp-input-field.success-input {
          border: 2px solid #10b981 !important;
          background-color: #f0fdf4;
          color: #065f46;
        }

        .otp-input-field.success-input:focus {
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12);
        }

        .otp-wrapper {
          position: relative;
          display: inline-block;
        }

        .otp-dot {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: ${inputSize / 5}px;
          height: ${inputSize / 5}px;
          background-color: #4ade80;
          border-radius: 50%;
          pointer-events: none;
          z-index: 1;
        }

        @media (max-width: 576px) {
          .otp-input-field {
            width: ${inputSize * 0.85}px;
            height: ${inputSize * 0.85}px;
            font-size: 1.5rem;
          }
        }
      `}</style>

      <div
        className='d-flex align-items-center justify-content-center'
        style={{ gap: '12px' }}
      >
        {Array.from({ length: noOfInputs }).map((_, index) => {
          const isFirst = index === 0
          const isFilled = Boolean(value[index])

          let inputClass = 'otp-input-field'
          if (hasError) {
            inputClass += ' error-input'
          } else if (success) {
            inputClass += ' success-input'
          } else if (isFilled) {
            inputClass += ' filled-input'
          }

          return (
            <div key={index} className='otp-wrapper'>
              <input
                ref={(el) => {
                  inputRefs.current[index] = el
                }}
                type={isSecure ? 'password' : 'text'}
                inputMode='numeric'
                pattern='[0-9]*'
                maxLength={1}
                value={value[index] || ''}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={handleFocus}
                onPaste={handlePaste}
                className={inputClass}
                aria-label={`OTP digit ${index + 1}`}
                style={{
                  WebkitTextSecurity: isSecure ? 'disc' : 'none',
                }}
              />
              {isFilled && !hasError && !success && <div className='otp-dot' />}
            </div>
          )
        })}
      </div>
    </>
  )
}

OTPInputField.propTypes = propTypes
OTPInputField.defaultProps = defaultProps

export default OTPInputField
